import { useState, useEffect } from 'react';
import { indexArray, sumArray } from '../../../../../library/array/array';
import { filterSoldOutProducts } from '../../shared/utils/phase';
import { getDishByPhaseStep } from '../../shared/utils/phase';
import { uiBlocker } from '../../../../../library/ui-blocker/ui-blocker';
import { getMealCount } from '../../shared/utils/meals';
import { postData } from '../../../../../utils/async';
import { TOASTR_TYPE_ERROR, TOASTR_TYPE_SUCCESS } from '../../../../../utils/toastr';
import { customToastr } from '../../../../../library/toastr/toastr';

export const DEFAULT_PRODUCT_INCREMENT = 1;

const uiBlock = uiBlocker();

export const useProducts = ({
    products,
    cartItemId,
    packageId,
    phaseSteps,
    activeFilters,
    loadProductsFromStorage,
    saveProductsToStorage,
    phaseStepsProducts,
    translations,
    savePhaseProductsToStorage,
    shouldUpdateOrderAfterProductsChange,
    saveProductApiEndpoint,
    removeProductsFromStorage,
    cartLink,
    packageLimitMeals,
}) => {
    const [pickedProducts, setPickedProducts] = useState(
        phaseSteps.reduce((prevPickedProducts, phaseStep) => ({ ...prevPickedProducts, [phaseStep]: [] }), {})
    );
    const [productAvailabilityMap, setProductAvailabilityMap] = useState({});
    const [matchProductsCount, setMatchProductsCount] = useState(null);
    const [filteredCategoryProductsByTasteFilters, setFilteredCategoryProductsByTasteFilters] =
        useState(phaseStepsProducts);

    const pickedProductsCount = sumArray(phaseSteps, (phaseStep) => getMealCount(pickedProducts[phaseStep]));
    const hasPickedProductExceedAvailability = phaseSteps.some((phaseStep) =>
        pickedProducts[phaseStep].some((pickedProduct) =>
            productAvailabilityMap[pickedProduct.id] ? productAvailabilityMap[pickedProduct.id].available < 0 : false
        )
    );

    // const hasPhaseStepMealsExceeded = pickedProductsCount > phaseStepLimit;
    const hasPhaseStepMealsCompleted = pickedProductsCount === packageLimitMeals;
    const remainingProductsToCompleteCount = packageLimitMeals - pickedProductsCount;

    useEffect(() => {
        const savedProducts = loadProductsFromStorage(cartItemId, null, packageId);

        const removeProductIds = [];

        if (!savedProducts) {
            return;
        }

        const newProductAvailabilityMap = indexArray(
            products,
            (product) => product.id,
            (product) => ({ available: product.available })
        );

        // Iterace nad všemi typy balíčku (krok fáze)
        Object.keys(savedProducts).forEach((phaseStep) => {
            const hasProducts = !!savedProducts[phaseStep];
            if (!hasProducts) {
                return;
            }
            savedProducts[phaseStep] = filterSoldOutProducts(
                savedProducts[phaseStep],
                removeProductIds,
                newProductAvailabilityMap,
                translations
            );
        });

        // Alespoň jeden produkt smazán => update local storage
        const shouldUpdateLocalStorage = removeProductIds.length > 0;
        if (shouldUpdateLocalStorage) {
            savePhaseProductsToStorage(cartItemId, null, packageId, savedProducts);
        }

        const savedPhaseStepProducts = phaseSteps.reduce(
            (prevSavedPhaseStepProducts, phaseStep) => ({
                ...prevSavedPhaseStepProducts,
                [phaseStep]: (savedProducts && savedProducts[phaseStep]) || [],
            }),
            {}
        );

        setPickedProducts(savedPhaseStepProducts);
    }, [cartItemId, packageId]);

    useEffect(() => {
        const newProductAvailabilityMap = indexArray(
            products,
            (product) => product.id,
            (product) => ({ available: product.available })
        );

        const updatedProductAvailabilityMap = { ...newProductAvailabilityMap };

        const phaseStepProducts = loadProductsFromStorage(cartItemId, null, packageId);
        if (phaseStepProducts) {
            Object.keys(phaseStepProducts).forEach((phaseStep) => {
                const availablePhaseStepProducts = phaseStepProducts[phaseStep].filter(
                    (product) => updatedProductAvailabilityMap[product.id]
                );
                availablePhaseStepProducts.forEach((product) => {
                    updatedProductAvailabilityMap[product.id] = {
                        available: updatedProductAvailabilityMap[product.id].available - product.amount,
                    };
                });
            });
        }

        setProductAvailabilityMap(updatedProductAvailabilityMap);
    }, [products]);

    useEffect(() => {
        if (!phaseStepsProducts) {
            return;
        }

        const updatedFilteredCategoryProductsByTasteFilters = phaseSteps.reduce(
            (prevUpdatedFilteredCategoryProductsByTasteFilters, phaseStep) => ({
                ...prevUpdatedFilteredCategoryProductsByTasteFilters,
                [phaseStep]: getProductsByActiveFilters(phaseStep),
            }),
            {}
        );

        setFilteredCategoryProductsByTasteFilters(updatedFilteredCategoryProductsByTasteFilters);
    }, [activeFilters]);

    useEffect(() => {
        if (!activeFilters || activeFilters.length === 0) {
            return;
        }

        const updatedMatchProductsCount = sumArray(
            phaseSteps,
            (phaseStep) => filteredCategoryProductsByTasteFilters[phaseStep].length
        );

        setMatchProductsCount(updatedMatchProductsCount);
    }, [activeFilters, filteredCategoryProductsByTasteFilters]);

    const onSendPickedProducts = (updatedProducts, triggeredFromButton = true) => {
        const dietCalculationSendOfferButton = document.getElementById('package-calculation-send-offer');

        const pickedProductsForApi = updatedProducts || pickedProducts;

        const products = Object.keys(pickedProductsForApi)
            .map((phaseStep) =>
                pickedProductsForApi[phaseStep].map((product) => ({
                    productId: product.id,
                    amount: product.amount,
                    phaseId: packageId,
                    dish: getDishByPhaseStep(phaseStep),
                }))
            )
            .reduce((sum, product) => sum.concat(product), []);

        const data = {
            cartItemId: cartItemId || null,
            phaseId: packageId,
            dietId: null,
            pickedProducts: products,
        };

        uiBlock.block(dietCalculationSendOfferButton);

        postData(saveProductApiEndpoint, data)
            .then((data) => {
                uiBlock.unblock(dietCalculationSendOfferButton);
                if (data.error) {
                    customToastr(TOASTR_TYPE_ERROR, data.message);
                } else {
                    customToastr(TOASTR_TYPE_SUCCESS, data.message);

                    if (!shouldUpdateOrderAfterProductsChange) {
                        window.location.href = cartLink;
                        removeProductsFromStorage(cartItemId, null, [packageId]);
                    } else if (shouldUpdateOrderAfterProductsChange && triggeredFromButton) {
                        window.location.href = cartLink;
                    }
                }
            })
            .catch((error) => {
                uiBlock.unblock(dietCalculationSendOfferButton);
                customToastr(TOASTR_TYPE_ERROR, error);
            });
    };

    const getProductsByActiveFilters = (phaseStep) => {
        const hasProducts = !!phaseStepsProducts && !!phaseStepsProducts[phaseStep];
        const hasActiveFilter = activeFilters && activeFilters.length > 0;

        if (!hasProducts || !hasActiveFilter) {
            return [...phaseStepsProducts[phaseStep]];
        }

        return phaseStepsProducts[phaseStep].filter((product) =>
            product.flavours.some((flavour) => activeFilters.includes(flavour))
        );
    };

    const onChangeProductAmountByButton = (product, isIncrementing, phaseStep) => {
        const increment = product.increment || DEFAULT_PRODUCT_INCREMENT;
        const amount = isIncrementing
            ? product.amount + increment
            : product.amount - increment < 0
            ? 0
            : product.amount - increment;

        onChangeProductAmount(product.id, amount, phaseStep, product.name);
    };

    const onChangeProductAmount = (productId, amount, phaseStep, title) => {
        setPickedProducts((prevProducts) => {
            const alreadyPicked = prevProducts[phaseStep].find((product) => product.id === productId);

            const updatedProducts = { ...prevProducts };
            const updatedProductAvailabilityMap = { ...productAvailabilityMap };
            let changedAmount = 0;

            if (!alreadyPicked && amount < 0) {
                return prevProducts;
            }

            if (alreadyPicked && amount === 0) {
                updatedProducts[phaseStep] = updatedProducts[phaseStep].filter((product) => product.id !== productId);
                changedAmount = -alreadyPicked.amount;
            } else if (alreadyPicked && amount > 0) {
                changedAmount = amount - alreadyPicked.amount;
                updatedProducts[phaseStep] = updatedProducts[phaseStep].map((product) => {
                    const productWithChangedAmount = product.id === productId;
                    return {
                        ...product,
                        amount: productWithChangedAmount ? amount : product.amount,
                        title: product.title,
                    };
                });
            } else if (!alreadyPicked && amount > 0) {
                updatedProducts[phaseStep].push({ id: productId, amount, title });
                changedAmount = amount;
            }

            const updatedPickedProductsCount = sumArray(phaseSteps, (phaseStep) =>
                getMealCount(updatedProducts[phaseStep])
            );
            const hasUpdatedPhaseStepMealsCompleted = updatedPickedProductsCount === packageLimitMeals;

            const onSaveProductsToStorage =
                shouldUpdateOrderAfterProductsChange && hasUpdatedPhaseStepMealsCompleted
                    ? () => onSendPickedProducts(updatedProducts, false)
                    : undefined;

            saveProductsToStorage(
                cartItemId,
                null,
                packageId,
                phaseStep,
                updatedProducts[phaseStep],
                onSaveProductsToStorage
            );

            updatedProductAvailabilityMap[productId] = {
                available: updatedProductAvailabilityMap[productId].available - changedAmount,
            };

            setProductAvailabilityMap(updatedProductAvailabilityMap, changedAmount);

            return updatedProducts;
        });
    };

    const onDecrementProduct = (product, phaseStep) => {
        onChangeProductAmountByButton(product, false, phaseStep);
    };

    const onIncrementProduct = (product, phaseStep) => {
        onChangeProductAmountByButton(product, true, phaseStep);
    };

    const onProductAmountChange = (productId, amount, phaseStep, title) => {
        onChangeProductAmount(productId, amount, phaseStep, title);
    };

    return {
        pickedProducts,
        onDecrementProduct,
        onIncrementProduct,
        onProductAmountChange,
        setPickedProducts,
        productAvailabilityMap,
        filteredCategoryProductsByTasteFilters,
        matchProductsCount,
        onSendPickedProducts,
        hasPickedProductExceedAvailability,
        remainingProductsToCompleteCount,
        hasPhaseStepMealsCompleted,
        pickedProductsCount,
    };
};
